<template>
  <div class="p-6">
    <!-- 页面标题和操作按钮 -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">功能管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理系统功能定义</p>
      </div>
      <button @click="handleCreateFeature"
        class="primary-btn gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        创建功能
      </button>
    </div>

    <!-- 功能列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <FeatureList :features="features" @edit="handleEditFeature" @delete="handleDeleteFeature" />
    </div>

    <!-- 创建/编辑功能 Modal -->
    <Modal :show="showFeatureModal" @close="closeFeatureModal" title="功能信息">
      <FeatureForm :feature="currentFeature" :isEdit="isEditMode" @submit="handleFeatureSubmit"
        @cancel="closeFeatureModal" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import FeatureList from "@/components/membership/FeatureList.vue";
import FeatureForm from "@/components/membership/FeatureForm.vue";
import Modal from "@/components/common/Modal.vue";
import { getAllFeaturesAPI, createFeatureAPI, updateFeatureAPI, deleteFeatureAPI } from "@/api/membership/feature";
import type { Feature, CreateFeatureRequest, UpdateFeatureRequest } from "@/types/membership/feature";
import { displayLocalizedText } from "@/types/membership/localized";
import toaster from "@/utils/toaster";

/**
 * 功能列表
 */
const features = ref<Feature[]>([])

/**
 * 当前操作的功能
 */
const currentFeature = ref<Feature | undefined>(undefined)

/**
 * 显示功能 Modal
 */
const showFeatureModal = ref(false)

/**
 * 是否为编辑模式
 */
const isEditMode = ref(false)

/**
 * 加载所有功能
 */
const loadFeatures = async () => {
  try {
    const response = await getAllFeaturesAPI()
    features.value = response.data.features
  } catch (error) {
    console.error('加载功能失败:', error)
  }
}

/**
 * 创建功能
 */
const handleCreateFeature = () => {
  currentFeature.value = undefined
  isEditMode.value = false
  showFeatureModal.value = true
}

/**
 * 编辑功能
 */
const handleEditFeature = (feature: Feature) => {
  currentFeature.value = feature
  isEditMode.value = true
  showFeatureModal.value = true
}

/**
 * 删除功能
 */
const handleDeleteFeature = async (feature: Feature) => {
  if (!confirm(`确定要删除功能"${displayLocalizedText(feature.name)}"吗？\n\n注意：删除功能可能会影响已配置该功能的套餐。`)) {
    return
  }
  try {
    const response = await deleteFeatureAPI(feature.id)
    if (response.status !== 200) {
      toaster.error(response.message || '删除失败')
      return
    }
    toaster.success('删除成功')
    await loadFeatures()
  } catch (error) {
    toaster.error('删除失败')
    console.error('删除功能失败:', error)
  }
}

/**
 * 提交功能表单
 */
const handleFeatureSubmit = async (data: CreateFeatureRequest | UpdateFeatureRequest) => {
  try {
    if (isEditMode.value && currentFeature.value) {
      await updateFeatureAPI(currentFeature.value.id, data as UpdateFeatureRequest)
    } else {
      await createFeatureAPI(data as CreateFeatureRequest)
    }
    await loadFeatures()
    closeFeatureModal()
  } catch (error) {
    console.error('保存功能失败:', error)
  }
}

/**
 * 关闭功能 Modal
 */
const closeFeatureModal = () => {
  showFeatureModal.value = false
  currentFeature.value = undefined
}

/**
 * 组件挂载时加载数据
 */
onMounted(async () => {
  await loadFeatures()
})
</script>
